export type StringTemplateData = {
  [key: string]: any;
};

/**
 * Given a string template like "Hello, {{name}}! Are you {{age}} years old?"
 *   and a data object like { name: 'John', age: 30 },
 *   replaces the placeholders with values from the data object.
 *
 * Can also pass in a function that will be called with the placeholder and any provided variants
 *   Example: "Your selection: {{paymentType[Translated1|Translated2]}}"
 *   data: { paymentType: (placeholder, variants) => `${placeholder}=${variants[0]}`; }
 *   result: "Your selection: paymentType=Translated1"
 */
export default function interpolate(
  str: string | undefined,
  data: StringTemplateData = {},
) {
  if (!str) {
    return '';
  }
  const regex = /\{\{\s*(?:[\w\-]|\[(?:[^\]])*\])*\s*\}\}/g;
  const parts = str.split(regex);
  const properties = str.match(regex) || [];
  const parameters = properties.map((prop) => {
    const [arg, rest] = prop
      .replace(/^\{\{/, '')
      .replace(/\}\}$/, '')
      .trim()
      .split('[');
    const variants =
      rest &&
      rest
        .split(']')[0]
        .split('|')
        .map((v): string => {
          return interpolate(v, data);
        });
    const value = data[arg];
    const valueType = typeof value;
    if (valueType === 'undefined' || value === null) {
      return '';
    }
    if (valueType === 'function') {
      return value(arg, variants);
    }
    if (valueType === 'object') {
      return Array.isArray(value) ? value.join(',') : JSON.stringify(value);
    }
    return `${value}`;
  });
  // String.raw({ raw: ['A', 'B', 'C'] }, '+', '=');  // 'A+B=C'
  return String.raw({ raw: parts }, ...parameters);
}
